// Minimal WebGL point renderer for the avatar. The CPU still computes every
// particle's position/size/alpha each frame (same physics as the 2D path); this
// module just blits them on the GPU as soft additive points, which is far faster
// than thousands of canvas arc() calls and gives a luminous glow everywhere.
//
// It renders a radial stage backdrop (quad) then the points. Blending switches
// between additive (dark stage → glow) and normal (light stage → dark ink).
// createGLRenderer returns null if WebGL is unavailable → caller uses 2D.

export interface GLFrame {
  bg: { r: number; g: number; b: number; a: number } | null; // 0..1 backdrop color
  additive: boolean;        // true → glow (dark stage), false → normal (light stage)
  count: number;
  positions: Float32Array;  // x,y per point in CSS pixels (length ≥ count*2)
  sizes: Float32Array;      // diameter in CSS px (length ≥ count)
  colors: Float32Array;     // r,g,b,a per point, 0..1 (length ≥ count*4)
}
export interface GLRenderer {
  resize(w: number, h: number, dpr: number): void;
  frame(f: GLFrame): void;
  dispose(): void;
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src); gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { gl.deleteShader(s); return null; }
  return s;
}
function program(gl: WebGLRenderingContext, vs: string, fs: string): WebGLProgram | null {
  const v = compile(gl, gl.VERTEX_SHADER, vs), f = compile(gl, gl.FRAGMENT_SHADER, fs);
  if (!v || !f) return null;
  const p = gl.createProgram();
  if (!p) return null;
  gl.attachShader(p, v); gl.attachShader(p, f); gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) { gl.deleteProgram(p); return null; }
  return p;
}

// Points: positions in CSS px (→ clip via logical resolution), gl_PointSize in
// framebuffer px (→ multiply by DPR).
const POINT_VS = `
attribute vec2 aPos; attribute float aSize; attribute vec4 aColor;
uniform vec2 uRes; uniform float uDpr; varying vec4 vColor;
void main(){
  vec2 clip = (aPos / uRes) * 2.0 - 1.0; clip.y = -clip.y;
  gl_Position = vec4(clip, 0.0, 1.0);
  gl_PointSize = aSize * uDpr; vColor = aColor;
}`;
const POINT_FS = `
precision mediump float; varying vec4 vColor;
void main(){
  float d = length(gl_PointCoord - vec2(0.5));
  float a = smoothstep(0.5, 0.0, d);
  gl_FragColor = vec4(vColor.rgb, vColor.a * a);
}`;
const BG_VS = `attribute vec2 aQuad; void main(){ gl_Position = vec4(aQuad, 0.0, 1.0); }`;
const BG_FS = `
precision mediump float; uniform vec2 uRes; uniform vec3 uColor; uniform float uAlpha;
void main(){
  vec2 uv = gl_FragCoord.xy / uRes; vec2 c = vec2(0.5, 0.55);
  float d = distance(uv, c) / 0.62;
  gl_FragColor = vec4(uColor, uAlpha * clamp(1.0 - d, 0.0, 1.0));
}`;

export function createGLRenderer(canvas: HTMLCanvasElement): GLRenderer | null {
  let gl: WebGLRenderingContext | null = null;
  try {
    gl = (canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: true, preserveDrawingBuffer: true })
      || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
  } catch { gl = null; }
  if (!gl) return null;

  const ptProg = program(gl, POINT_VS, POINT_FS);
  const bgProg = program(gl, BG_VS, BG_FS);
  if (!ptProg || !bgProg) return null;

  const posBuf = gl.createBuffer()!, sizeBuf = gl.createBuffer()!, colBuf = gl.createBuffer()!, quadBuf = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW); // big covering triangle

  const aPos = gl.getAttribLocation(ptProg, 'aPos');
  const aSize = gl.getAttribLocation(ptProg, 'aSize');
  const aColor = gl.getAttribLocation(ptProg, 'aColor');
  const uResPt = gl.getUniformLocation(ptProg, 'uRes');
  const uDpr = gl.getUniformLocation(ptProg, 'uDpr');
  const aQuad = gl.getAttribLocation(bgProg, 'aQuad');
  const uResBg = gl.getUniformLocation(bgProg, 'uRes');
  const uColor = gl.getUniformLocation(bgProg, 'uColor');
  const uAlpha = gl.getUniformLocation(bgProg, 'uAlpha');

  let cssW = 300, cssH = 300, dprV = 1, W = canvas.width, H = canvas.height;

  return {
    resize(w, h, dpr) {
      cssW = w; cssH = h; dprV = dpr;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
      W = canvas.width; H = canvas.height;
      gl!.viewport(0, 0, W, H);
    },
    frame(f) {
      const g = gl!;
      g.viewport(0, 0, W, H);
      g.disable(g.DEPTH_TEST);
      g.clearColor(0, 0, 0, 0);
      g.clear(g.COLOR_BUFFER_BIT);
      g.enable(g.BLEND);

      // Stage backdrop (normal alpha blending). gl_FragCoord is in framebuffer px.
      if (f.bg && f.bg.a > 0) {
        g.blendFunc(g.SRC_ALPHA, g.ONE_MINUS_SRC_ALPHA);
        g.useProgram(bgProg);
        g.uniform2f(uResBg, W, H);
        g.uniform3f(uColor, f.bg.r, f.bg.g, f.bg.b);
        g.uniform1f(uAlpha, f.bg.a);
        g.bindBuffer(g.ARRAY_BUFFER, quadBuf);
        g.enableVertexAttribArray(aQuad);
        g.vertexAttribPointer(aQuad, 2, g.FLOAT, false, 0, 0);
        g.drawArrays(g.TRIANGLES, 0, 3);
      }

      // Points: additive glow (dark stage) or normal blend (light stage).
      g.blendFunc(g.SRC_ALPHA, f.additive ? g.ONE : g.ONE_MINUS_SRC_ALPHA);
      g.useProgram(ptProg);
      g.uniform2f(uResPt, cssW, cssH);
      g.uniform1f(uDpr, dprV);

      g.bindBuffer(g.ARRAY_BUFFER, posBuf);
      g.bufferData(g.ARRAY_BUFFER, f.positions.subarray(0, f.count * 2), g.DYNAMIC_DRAW);
      g.enableVertexAttribArray(aPos);
      g.vertexAttribPointer(aPos, 2, g.FLOAT, false, 0, 0);

      g.bindBuffer(g.ARRAY_BUFFER, sizeBuf);
      g.bufferData(g.ARRAY_BUFFER, f.sizes.subarray(0, f.count), g.DYNAMIC_DRAW);
      g.enableVertexAttribArray(aSize);
      g.vertexAttribPointer(aSize, 1, g.FLOAT, false, 0, 0);

      g.bindBuffer(g.ARRAY_BUFFER, colBuf);
      g.bufferData(g.ARRAY_BUFFER, f.colors.subarray(0, f.count * 4), g.DYNAMIC_DRAW);
      g.enableVertexAttribArray(aColor);
      g.vertexAttribPointer(aColor, 4, g.FLOAT, false, 0, 0);

      g.drawArrays(g.POINTS, 0, f.count);
    },
    dispose() {
      const g = gl!;
      g.deleteBuffer(posBuf); g.deleteBuffer(sizeBuf); g.deleteBuffer(colBuf); g.deleteBuffer(quadBuf);
      g.deleteProgram(ptProg); g.deleteProgram(bgProg);
    },
  };
}
